import { Button, Textarea } from '@bbl-digital/snorre'
import { useFormik } from 'formik'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ErrorText from 'shared/components/ErrorText'
import { ISendSmsSettings, ISmsMessage } from 'shared/models/Message'
import { RequiredValidation } from 'shared/utils/validations'
import { ActionButtons, ScrollWrapper } from '../styles'
import * as Yup from 'yup'
import {
  SEND_SMS_CREATE_DRAFT_REQUESTED,
  SEND_SMS_UPDATE_DRAFT_REQUESTED,
} from 'shared/store/sendMessage/sms/actionTypes'
import SmsInformation from './SmsInformation'
import RecipientInput from '../../SendToRecipients/RecipientInput'
import { MessageTypeEnum } from 'Modules/Communication/shared/models/Recipients'
import { RecipientTypeEnum } from 'Modules/Communication/shared/models/Inbox'
import useInitFrequentRecipients from 'Modules/Communication/shared/hooks/useInitFrequentRecipients'
import { getSendSmsRecipients } from 'shared/store/sendMessage/sms/selectors'
import { getBoardFunctionForActiveUser } from 'shared/store/board/selectors'
import { getUserFullname } from 'shared/store/user/selectors'
import { getSmsSignature } from 'Modules/Communication/CommunicationSettings/shared/utils/signature-helpers'
import { getUniqueValidRecipientsCount } from '../../shared/utils/send-sms-helper'

interface IProps {
  processing: boolean
  error: string | null
  message: ISmsMessage | null
  messageId: string | null
  settings: ISendSmsSettings
  onSubmit: (message: string) => void
  onSaveDraft: () => void
}

const validationSchema = Yup.object().shape({
  content: RequiredValidation,
})

const SendSmsForm: FC<IProps> = ({
  processing,
  error,
  message,
  messageId,
  settings,
  onSubmit,
  onSaveDraft,
}) => {
  useInitFrequentRecipients(MessageTypeEnum.SMS)
  const [maxLimitReached, setMaxLimitReached] = useState(false)
  const recipients = useSelector(getSendSmsRecipients)
  const role = useSelector(getBoardFunctionForActiveUser)
  const userFullName = useSelector(getUserFullname)
  const dispatch = useDispatch()
  const defaultContent = getSmsSignature(role, userFullName)

  const { values, setFieldValue, submitForm, setFieldTouched } =
    useFormik<ISmsMessage>({
      validationSchema,
      initialValues: {
        content: message?.content || defaultContent,
      },
      onSubmit: (e) => onSubmit(e.content),
    })

  useEffect(() => {
    if (!messageId)
      dispatch({
        type: SEND_SMS_CREATE_DRAFT_REQUESTED,
        payload: defaultContent,
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const updateDraft = () => {
    const value = values.content

    if (messageId && value !== message?.content)
      dispatch({
        type: SEND_SMS_UPDATE_DRAFT_REQUESTED,
        payload: { value, id: messageId },
      })
  }

  return (
    <>
      <ScrollWrapper>
        <RecipientInput
          label="Til"
          messageType={MessageTypeEnum.SMS}
          recipientType={RecipientTypeEnum.To}
        />

        <Textarea
          value={values.content}
          label="Melding"
          onChange={(e) => setFieldValue('content', e)}
          onBlur={() => {
            updateDraft()
            setFieldTouched('content')
          }}
          rows={8}
          maxLength={settings.maxLengthSms}
        />

        <SmsInformation
          contentChars={values.content.length}
          numberOfRecipients={getUniqueValidRecipientsCount(recipients)}
          settings={settings}
          onMaxLimitReached={(e) => setMaxLimitReached(e)}
        />
      </ScrollWrapper>

      <ActionButtons>
        <Button
          onClick={onSaveDraft}
          outline
          disabled={processing}
          trackingName="Kommunikasjon"
          trackingEvent="SMS: Lagre utkast"
        >
          Lagre utkast
        </Button>
        <Button
          type="submit"
          onClick={submitForm}
          disabled={maxLimitReached}
          loading={processing}
          trackingName="Kommunikasjon"
          trackingEvent="SMS: Send SMS"
        >
          Send
        </Button>
      </ActionButtons>
      {error && <ErrorText withIcon={false} text={error} />}
    </>
  )
}

export default SendSmsForm
